// The Photos part of the identity page, connected (PRD-0031 slice 03).
//
// The grid under `components/identities/` stays pure: this resolves the
// auth-gated full source images into blob URLs and opens the shared document
// overlay focused on the face that put the photo in the list.

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDocumentOverlay } from '@/components/document-overlay/DocumentDetailOverlayContext'
import { IdentityMediaPager } from '@/components/identities/IdentityMediaPager'
import {
  IdentityPhotosGrid,
  type IdentityPhotoTile,
} from '@/components/identities/IdentityPhotosGrid'
import { Button } from '@/components/ui/button'
import { useAuthedImageUrls } from '@/features/face-matching/hooks/useAuthedImageUrls'
import { PHOTOS_PAGE_SIZE, useIdentityPhotos } from './hooks/useIdentityPhotos'

export function IdentityPhotos({ caseId, personId }: { caseId: string; personId: string }) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const query = useIdentityPhotos(caseId, personId, page)
  const photos = query.data?.photos ?? []
  const total = query.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PHOTOS_PAGE_SIZE))
  const images = useAuthedImageUrls(photos.map(photo => photo.source_url))
  const overlay = useDocumentOverlay()

  useEffect(() => {
    if (query.isSuccess && !query.isPlaceholderData && page > totalPages) setPage(totalPages)
  }, [page, query.isSuccess, query.isPlaceholderData, totalPages])

  const tiles: IdentityPhotoTile[] = photos.map(photo => ({
    documentId: photo.document_id,
    faceId: photo.face_id,
    thumbnailUrl: photo.source_url ? (images[photo.source_url] ?? null) : null,
    documentName: photo.document_name,
    page: photo.page,
    uploadedAt: photo.uploaded_at,
    processedAt: photo.processed_at,
  }))

  return (
    <div className="space-y-3">
      {query.isError && (
        <div role="alert" className="flex flex-wrap items-center gap-3 text-sm text-destructive">
          <span>{t('identity.photos.loadError')}</span>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t('identity.photos.retry')}
          </Button>
        </div>
      )}
      {(!query.isError || tiles.length > 0) && (
        <IdentityPhotosGrid
          photos={tiles}
          loading={query.isFetching}
          onOpen={photo =>
            overlay.open({ documentId: photo.documentId, page: photo.page, faceId: photo.faceId })
          }
        />
      )}
      <IdentityMediaPager
        pagination={{
          label: t('identity.detail.sections.photos'),
          page: query.data?.page ?? page,
          pageSize: PHOTOS_PAGE_SIZE,
          total,
          loading: query.isFetching,
        }}
        onPageChange={setPage}
      />
    </div>
  )
}
